import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import companyController from "../controllers/companyController";
import LegalEntityIcon from "../icons/registrationProcess/LegalEntityIcon";
import RegistrationFormIcon from "../icons/registrationProcess/RegistrationFormIcon";
import UploadIcon from "../icons/registrationProcess/UploadIcon";
import UserProfileIcon from "../icons/registrationProcess/UserProfileIcon";
import SignInForm from "../components/SignIn/SignInForm";
import { userService } from "../services/userService";
import {
  cleanDocument,
  fileToPickedDocument,
  getErrorMessage,
  isApiError,
} from "./registrationHelpers";
import { FooterTerms, RegistrationLayout, TextField } from "./RegistrationFlow";
import Utils from "../utils/Utils";
import { translateFieldLabel, translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";


const RESUBMIT_DOCUMENT_FIELDS = [
  {
    key: "ownerDocument",
    label: "Owner ID / Passport",
    labelId: "txt_common_owner_id_passport",
    objectName: "owner-id-passport",
    type: "ID",
  },
  {
    key: "legalEntityDocument",
    label: "Proof of Legal Entity",
    labelId: "input_legal_entity_proof",
    objectName: "proof-of-legal-entity",
    type: "PROOF_OF_LEAGALITY",
  },
];

const LOCKED_ATTACHMENT_STATUSES = new Set([
  "APPROVED",
  "VERIFIED",
  "PENDING",
  "DEFAULT",
]);

const GREEN_ATTACHMENT_STATUSES = new Set(["APPROVED", "VERIFIED"]);

const DOCUMENT_STATUS_LABELS = {
  APPROVED: "txt_business_approved",
  VERIFIED: "txt_web_vendor_document_verified",
  PENDING: "txt_common_pending",
  DEFAULT: "txt_default",
  REJECTED: "txt_admin_rejected",
};

const normalizeAttachmentStatus = (status) =>
  String(status || "DEFAULT")
    .trim()
    .toUpperCase();

const toDisplayLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "Default";

const getDocumentHref = (dataUrl) => {
  if (!dataUrl) return "";
  if (/^(data:|https?:|blob:)/i.test(dataUrl)) return dataUrl;
  return `data:application/octet-stream;base64,${dataUrl}`;
};

const getResubmitToken = (search) => {
  const rawSearch = String(search || "");
  const marker = "data=";
  const markerIndex = rawSearch.indexOf(marker);

  if (markerIndex === -1) return "";

  return rawSearch.slice(markerIndex + marker.length);
};

function ResubmitCompanyAttachments() {
  useTranslationSnapshot();
  const location = useLocation();
  const resubmitData = getResubmitToken(location.search);
  const [decryptedUrlData, setDecryptedUrlData] = useState(null);
  // const [BPID, setBPID] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [documents, setDocuments] = useState({});
  const [loadingField, setLoadingField] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [CompAttachments, setCompAttachments] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(userService.getJwt()),
  );

  useEffect(() => {
    const encryptedData = Utils.extractResubmitionDta(
      decodeURIComponent(window.location.href),
    );

    try {
      setDecryptedUrlData(Utils.AESDecryptBody(encryptedData));
      const decryptedData = Utils.AESDecryptBody(encryptedData);
      const companyID =
        decryptedData.match(/"companyID"\s*:\s*"([^"}]+)/)?.[1] ?? null;

      setDecryptedUrlData(companyID);
    } catch (error) {
      console.log("[Resubmit Company Attachments] decrypt error", error);
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!decryptedUrlData) return;
    if (!userService.getJwt()) return;
    try {
      console.log(
        "BPID",
        Utils.parseJwt(userService.getJwt())?.userDetails?.[decryptedUrlData]
          ?.busienssProfileID ?? null,
      );
      // setBPID(
      //   Utils.parseJwt(userService.getJwt())?.userDetails?.[decryptedUrlData]
      //     ?.busienssProfileID ?? null,
      // );
      try {
        const CompData = async () => {
          const DataRes = await companyController.getCompanyDetails({
            Authorization: userService.getJwt(),
            copmayRequest: {
              businessProfileID:
                Utils.parseJwt(userService.getJwt())?.userDetails?.[
                  decryptedUrlData
                ]?.busienssProfileID ?? null,
              companyId: decryptedUrlData,
            },
          });
          console.log("data", DataRes?.data?.documentationVerification);
          setCompAttachments(
            Array.isArray(DataRes?.data?.documentationVerification)
              ? DataRes.data.documentationVerification
              : [],
          );
        };
        CompData();
      } catch (error) {}
    } catch (error) {}
    console.log(
      "[Resubmit Company Attachments] decrypted URL data",
      decryptedUrlData,
    );
  }, [decryptedUrlData]);

  const handleFile = async (field, file) => {
    setLoadingField(field.key);
    try {
      const document = await fileToPickedDocument(file, {
        attachmentType: field.type,
        countryName: "Lebanon",
        label: field.label,
        objectName: field.objectName,
        // registrationNumber: taxNumber || null,
        userName: Utils.parseJwt(userService.getJwt()).username,
      });
      setDocuments((currentDocuments) => ({
        ...currentDocuments,
        [field.key]: document,
      }));
    } catch (error) {
      toast.error(translateText(getErrorMessage(error, "Failed to select file.")));
    } finally {
      setLoadingField("");
    }
  };

  const getCurrentAttachment = (field) =>
    CompAttachments.find(
      (attachment) =>
        String(attachment?.attachmentType || "").toUpperCase() === field.type,
    );

  const getAttachmentStatusClassName = (status) => {
    const normalizedStatus = normalizeAttachmentStatus(status);

    if (GREEN_ATTACHMENT_STATUSES.has(normalizedStatus)) {
      return "is-green";
    }

    if (LOCKED_ATTACHMENT_STATUSES.has(normalizedStatus)) {
      return "is-neutral";
    }

    return "is-red";
  };

  const submitAttachments = async () => {
    if (!resubmitData) {
      toast.error(translateLabel("txt_common_this_resubmit_link_is_missing_required_data", "This resubmit link is missing required data."));
      return;
    }

    const selectedDocuments = RESUBMIT_DOCUMENT_FIELDS.map(
      (field) => documents[field.key],
    ).filter(Boolean);

    if (selectedDocuments.length === 0) {
      toast.error(translateLabel("txt_common_please_upload_at_least_one_document_to_resubmit", "Please upload at least one document to resubmit."));
      return;
    }

    try {
      setSubmitting(true);
      const customObject = selectedDocuments.map((document) =>
        cleanDocument({
          ...document,
          registrationNumber: taxNumber || document.registrationNumber,
          userName: vendorName || document.userName,
        }),
      );
      const payload = {
        attachmentType: "COMPANY_LOGO",
        customObject,
        companyID: decryptedUrlData,
        // vendorName: vendorName.trim() || null,
      };
      console.log("payload", payload);
      const response = await companyController.updateAddCompanyAttachments({
        Authorization: userService.getJwt(),
        addAttachments: payload,
      });

      if (isApiError(response)) {
        throw response;
      }

      setSubmitted(true);
      toast.success(translateLabel("txt_common_documents_resubmitted_successfully", "Documents resubmitted successfully."));
    } catch (error) {
      console.log("[Resubmit Company Attachments] error", error);
      toast.error(translateText(getErrorMessage(error, "Could not resubmit documents.")));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <RegistrationLayout
        title={translateLabel("txt_web_vendor_sign_in_to_resubmit_documents", "Sign In to Resubmit Documents")}
        icon={<RegistrationFormIcon />}
        className="registration-documents-page"
      >
        <SignInForm onSuccess={() => setIsAuthenticated(true)} />
        <FooterTerms />
      </RegistrationLayout>
    );
  }

  return (
    <RegistrationLayout
      title={translateLabel("txt_common_resubmit_legal_documents", "Resubmit Legal Documents")}
      icon={<RegistrationFormIcon />}
      className="registration-documents-page"
    >
      {submitted ? (
        <div className="registration-resubmit-success">
          <h2>{translateLabel("txt_common_documents_submitted", "Documents Submitted")}</h2>
          <p>{translateLabel("txt_common_thank_you_your_updated_legal_documents_were_sent_for_review", "Thank you. Your updated legal documents were sent for review.")}</p>
        </div>
      ) : (
        <div className="registration-form">
          <p className="registration-centered-copy">
            {translateLabel("txt_web_vendor_resubmit_documents_description", "Upload the missing or corrected vendor legal documents requested by the review team.")}
          </p>
          <TextField
            label={translateLabel("txt_common_company_name", "Company Name")}
            icon={<UserProfileIcon size={21.78} />}
          >
            <input
              value={vendorName}
              onChange={(event) => setVendorName(event.target.value)}
              placeholder={translateLabel("txt_common_company_name", "Company name")}
            />
          </TextField>
          {RESUBMIT_DOCUMENT_FIELDS.map((field) => {
            const currentAttachment = getCurrentAttachment(field);
            const currentStatus = normalizeAttachmentStatus(
              currentAttachment?.attachmentStatus,
            );
            const isLocked = LOCKED_ATTACHMENT_STATUSES.has(currentStatus);
            const documentHref = getDocumentHref(currentAttachment?.dataUrl);
            const selectedDocumentLabel = documents[field.key]?.label;
            const displayedDocumentLabel = selectedDocumentLabel === field.label
              ? translateFieldLabel(field.labelId, field.label)
              : selectedDocumentLabel;

            return (
              <div
                key={field.key}
                className={`registration-attachment-item ${
                  isLocked ? "is-locked" : ""
                }`}
              >
                <div className="registration-attachment-current">
                  <div>
                    <span>{translateFieldLabel(field.labelId, field.label)}</span>
                    {documentHref ? (
                      <a href={documentHref} target="_blank" rel="noreferrer">
                        {translateLabel("lnk_web_vendor_view_current_document", "View current document")}
                      </a>
                    ) : null}
                  </div>
                  <strong
                    className={`registration-attachment-status ${getAttachmentStatusClassName(
                      currentStatus,
                    )}`}
                  >
                    {DOCUMENT_STATUS_LABELS[currentStatus]
                      ? translateLabel(DOCUMENT_STATUS_LABELS[currentStatus], toDisplayLabel(currentStatus))
                      : toDisplayLabel(currentStatus)}
                  </strong>
                </div>
                <label className="registration-upload">
                  <input
                    type="file"
                    aria-label={translateFieldLabel(field.labelId, field.label)}
                    accept="image/*,application/pdf"
                    disabled={isLocked}
                    onChange={(event) =>
                      handleFile(field, event.target.files?.[0])
                    }
                  />
                  <div>
                    <UploadIcon size={22} />
                    <strong>
                      {isLocked
                        ? translateLabel("txt_web_vendor_no_document_changes_needed", "No changes needed")
                        : loadingField === field.key
                          ? translateLabel("txt_common_reading_file", "Reading file...")
                          : displayedDocumentLabel || translateLabel("txt_common_choose_file", "Choose file")}
                    </strong>
                    {documents[field.key]?.previewUri ? (
                      <img src={documents[field.key].previewUri} alt="" />
                    ) : null}
                  </div>
                </label>
              </div>
            );
          })}
          <TextField label={translateLabel("input_tax_number", "TAX Number")} icon={<LegalEntityIcon size={22} />}>
            <input
              type="text"
              inputMode="numeric"
              value={taxNumber}
              onChange={(event) =>
                setTaxNumber(event.target.value.replace(/\D/g, ""))
              }
              placeholder={translateLabel("input_tax_number", "Tax number")}
            />
          </TextField>
          <button
            type="button"
            className="registration-primary"
            disabled={submitting}
            onClick={submitAttachments}
          >
            {submitting ? translateLabel("btn_submitting", "Submitting...") : translateLabel("txt_common_submit_documents", "Submit Documents")}
          </button>
        </div>
      )}
      <FooterTerms />
    </RegistrationLayout>
  );
}

export default ResubmitCompanyAttachments;
